import React from 'react'

// Liquid-glass button (trial). The glass distortion uses an SVG displacement
// filter via backdrop-filter:url(#container-glass) — renders only in
// Chromium-based browsers. Safari and Firefox gracefully fall back to the
// plain translucent background because they ignore the url() form of
// backdrop-filter. Self-contained: no shadcn, no cva, no @radix-ui, no deps.

type LiquidButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>

// The SVG filter definition that drives the glass distortion.
// feTurbulence → feGaussianBlur → feDisplacementMap → feGaussianBlur produces
// the characteristic liquid-glass refraction effect.
// Duplicate identical <filter id="container-glass"> defs across multiple
// LiquidButton instances are harmless — the browser uses the first match.
function GlassFilter() {
  return (
    <svg
      style={{ display: 'none' }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id="container-glass" x="-10%" y="-10%" width="120%" height="120%" colorInterpolationFilters="sRGB">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.05"
            numOctaves="2"
            seed="2"
            result="turbulence"
          />
          <feGaussianBlur
            in="turbulence"
            stdDeviation="2"
            result="blurredTurbulence"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="blurredTurbulence"
            scale="70"
            xChannelSelector="R"
            yChannelSelector="B"
            result="displaced"
          />
          <feGaussianBlur
            in="displaced"
            stdDeviation="4"
            result="finalBlur"
          />
        </filter>
      </defs>
    </svg>
  )
}

export function LiquidButton({ className, children, style, type, ...props }: LiquidButtonProps) {
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 18px',
    borderRadius: 'var(--r-md, 10px)',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '14px',
    color: 'var(--ink)',
    background: 'rgba(255,255,255,0.18)',
    // Fallback translucent look for non-Chromium browsers
    WebkitBackdropFilter: 'blur(8px)',
    backdropFilter: 'blur(8px)',
    overflow: 'hidden',
    ...style,
  }

  // Glass rim — layered inset box-shadow that creates the characteristic
  // liquid-glass border highlight and inner shadow depth
  const rimShadow =
    'inset 0 1px 0 rgba(255,255,255,0.55), ' +
    'inset 0 -1px 0 rgba(0,0,0,0.08), ' +
    'inset 1px 0 0 rgba(255,255,255,0.35), ' +
    'inset -1px 0 0 rgba(255,255,255,0.20), ' +
    '0 2px 8px rgba(0,0,0,0.10), ' +
    '0 1px 2px rgba(0,0,0,0.06)'

  return (
    <>
      <GlassFilter />
      <button
        // Default to "button" so the element is safe inside forms
        type={type ?? 'button'}
        className={className}
        style={containerStyle}
        {...props}
      >
        {/* Layer 1 — glass rim (inset box-shadow giving the glass border) */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            boxShadow: rimShadow,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Layer 2 — backdrop displacement (Chromium-only glass distortion).
            Falls back invisibly in other browsers — the plain translucent
            background from the outer button remains visible instead. */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            // url(#container-glass) is only honoured by Chromium; other engines
            // ignore url() references in backdrop-filter and fall back silently.
            backdropFilter: 'url(#container-glass) blur(4px)',
            WebkitBackdropFilter: 'url(#container-glass) blur(4px)',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        {/* Layer 3 — label/children above the glass layers */}
        <span
          style={{
            position: 'relative',
            zIndex: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {children}
        </span>
      </button>
    </>
  )
}
